import React, { useEffect, useState } from 'react';
import azureDevOpsService from './services/azureDevOpsService';

export default function KpiTopFailedTests() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState('');

  // Pipelines disponibles
  const pipelines = [
    { id: 601, name: 'RF Admin API' },
    { id: 602, name: 'RF Admin Browser' },
    { id: 603, name: 'RF Config API' },
    { id: 604, name: 'RF Config Browser' },
    { id: 605, name: 'RF DE API' },
    { id: 606, name: 'RF DE Browser' },
    { id: 607, name: 'RF Pelias' },
    { id: 608, name: 'RF DP API' },
    { id: 609, name: 'RF DP Browser' },
    { id: 610, name: 'RF Investigation API' },
    { id: 611, name: 'RF Agentique API' },
    { id: 612, name: 'RF Agentique Browser' },
    { id: 613, name: 'RF Assistant API' },
    { id: 614, name: 'RF Assistant Browser' },
    { id: 615, name: 'RF OSINT API' },
    { id: 616, name: 'RF OSINT Browser' },
    { id: 303, name: 'ReadyAPI - Admin Tests' },
    { id: 304, name: 'ReadyAPI - Data Preparation' },
    { id: 305, name: 'ReadyAPI - Data Exploration' },
    { id: 355, name: 'ReadyAPI - Investigation' }
  ];

  useEffect(() => {
    async function fetchData() {
      if (!selectedPipeline) {
        setData([]);
        return;
      }

      setLoading(true);
      
      // Date il y a 30 jours
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      // Pipeline sélectionné uniquement
      const p = pipelines.find(pl => pl.id === parseInt(selectedPipeline));
      if (!p) {
        setLoading(false);
        return;
      }
      
      // Récupérer les résultats pour le pipeline sélectionné (builds -> runs -> results)
      const allTestResults = [];
      
      try {
        const perPipelineBuildCap = 50; // augmenté pour avoir plus de données sur 1 pipeline
        const perBuildRunCap = 5;
        try {
          const buildsEndpoint = `/build/builds?definitions=${p.id}&minTime=${thirtyDaysAgo.toISOString()}&$top=${perPipelineBuildCap}&queryOrder=finishTimeDescending&api-version=7.1`;
          const buildsResp = await azureDevOpsService.makeApiCall(buildsEndpoint, { cacheTtlMs: 30 * 60 * 1000 });
          const builds = buildsResp.value || [];
          console.log(`[${p.name}] builds: ${builds.length}`);

          for (const b of builds) {
            try {
              const runs = await azureDevOpsService.getTestRunsByBuildId(b.id);
              const completedRuns = (runs || [])
                .filter(r => r.state === 'Completed' && (r.totalTests || 0) > 0)
                .sort((a, b) => new Date(b.completedDate || b.lastUpdatedDate) - new Date(a.completedDate || a.lastUpdatedDate))
                .slice(0, perBuildRunCap);
              console.log(`[${p.name}] build ${b.id} runs: ${completedRuns.length}`);

              for (const run of completedRuns) {
                try {
                  const resultsEndpoint = `/test/runs/${run.id}/results?api-version=7.1-preview.6`;
                  const resultsResponse = await azureDevOpsService.makeApiCall(resultsEndpoint, { cacheTtlMs: 30 * 60 * 1000 });
                  const results = resultsResponse.value || [];
                  allTestResults.push(...results.map(t => ({
                    ...t,
                    runName: run.name,
                    runId: run.id,
                    pipelineName: p.name,
                  })));
                } catch (e) {
                  console.warn(`Résultats introuvables pour run ${run.id} (${p.name})`, e);
                }
              }
            } catch (e) {
              console.warn(`Runs introuvables pour build ${b.id} (${p.name})`, e);
            }
          }
        } catch (e) {
          console.warn(`Erreur builds pour pipeline ${p.name}`, e);
        }
      } catch (error) {
        console.error('Erreur récupération via pipeline:', error);
      }
      
      console.log('Total test results récupérés:', allTestResults.length);
      
      // Grouper par nom de test
      const testStats = {};
      allTestResults.forEach(test => {
        // Essayer différents champs pour trouver le nom du test
        const testName = test.automatedTestName || 
                        test.testCaseTitle || 
                        test.testCase?.name ||
                        test.automatedTestStorage ||
                        `Test-${test.id}`;
        
        if (!testStats[testName]) {
          testStats[testName] = {
            name: testName,
            totalRuns: 0,
            failures: 0,
            pipelines: new Set(),
          };
        }
        testStats[testName].totalRuns++;
  testStats[testName].pipelines.add(test.pipelineName);
        if (test.outcome === 'Failed') {
          testStats[testName].failures++;
        }
      });
      
      console.log('Tests uniques trouvés:', Object.keys(testStats).length);
      
      // Calculer le taux d'échec et trier
      const testArray = Object.values(testStats)
        .map(test => ({
          ...test,
          pipelineNames: Array.from(test.pipelines).join(', '),
          failureRate: test.totalRuns > 0 ? (test.failures / test.totalRuns * 100).toFixed(2) : 0,
        }))
        .filter(test => test.failures > 0) // Garder seulement les tests qui ont échoué
        .sort((a, b) => b.failureRate - a.failureRate) // Trier par taux d'échec décroissant
        .slice(0, 20); // Top 20
      
      setData(testArray);
      setLoading(false);
    }
    fetchData();
  }, [selectedPipeline]);

  return (
    <div className="bg-white rounded shadow p-6">
      <h2 className="text-lg font-bold mb-4">Top des tests les plus échoués (30 derniers jours)</h2>
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sélectionner un pipeline
        </label>
        <select
          value={selectedPipeline}
          onChange={(e) => setSelectedPipeline(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">-- Choisir un pipeline --</option>
          {pipelines.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      {/* Loading State */}
      {loading && (
        <div className="py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Analyse en cours...</p>
        </div>
      )}

      {/* Results */}
      {!loading && selectedPipeline && (
        <div>
          {data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">#</th>
                    <th className="text-left px-4 py-2 font-semibold">Nom du test</th>
                    <th className="text-left px-4 py-2 font-semibold">Pipeline</th>
                    <th className="text-right px-4 py-2 font-semibold">Nbr d'échecs</th>
                    <th className="text-right px-4 py-2 font-semibold">Total exécutions</th>
                    <th className="text-right px-4 py-2 font-semibold">Taux d'échec</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((test, idx) => (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">{idx + 1}</td>
                      <td className="px-4 py-2 max-w-md truncate" title={test.name}>
                        {test.name}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600" title={test.pipelineNames}>
                        {test.pipelineNames}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-red-600">
                        {test.failures}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600">
                        {test.totalRuns}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className={`font-semibold ${
                          test.failureRate > 50 ? 'text-red-600' : 
                          test.failureRate > 20 ? 'text-orange-500' : 
                          'text-yellow-600'
                        }`}>
                          {test.failureRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              Aucun échec de test détecté sur les 30 derniers jours.
            </div>
          )}
        </div>
      )}

      {/* Initial State */}
      {!loading && !selectedPipeline && (
        <div className="text-center py-6 text-gray-600">
          Choisissez un pipeline pour commencer l'analyse.
        </div>
      )}
    </div>
  );
}
